import React from 'react'
import classNames from 'classnames/bind'
import { Head, usePage } from '@inertiajs/react'
import HeatMap from '@uiw/react-heat-map'
import AdminLayout from '@/admin/shell/layouts/AdminLayout'
import KpiCard from '../components/KpiCard'
import type { DashboardPageProps } from '../types'
import styles from '../Dashboard.module.scss'

const cx = classNames.bind(styles)

// ── KPI icons ─────────────────────────────────────────────────────────────────
const svgBase = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }

const UsersIcon  = () => <svg {...svgBase}><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.85"/></svg>
const NotesIcon  = () => <svg {...svgBase}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h8M8 9h2"/></svg>
const SubsIcon   = () => <svg {...svgBase}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
const ActiveIcon = () => <svg {...svgBase}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>

// ── Page ──────────────────────────────────────────────────────────────────────
function DashboardPage({ userCount, noteCount, subscriptionCount, activeSubscriptionCount, activityHeatmap }: DashboardPageProps) {
  const pageProps = usePage().props as Record<string, unknown>
  const auth = pageProps.auth as { user?: { name?: string } } | undefined
  const firstName = (auth?.user?.name ?? 'Admin').split(' ')[0]

  const now = new Date()
  const dateStr = now.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  })

  const heatmapEnd = new Date()
  const heatmapStart = new Date()
  heatmapStart.setFullYear(heatmapStart.getFullYear() - 1)
  const heatmapValue = activityHeatmap.map(e => ({ date: e.date.replace(/-/g, '/'), count: e.count }))

  return (
    <>
      <Head title="Admin — Overview" />

      {/* Hero */}
      <section className={cx('hero')}>
        <div className={cx('heroText')}>
          <span className={cx('eyebrow')}>{dateStr}</span>
          <h1 className={cx('heroTitle')}>Welcome back, <span className={cx('accent')}>{firstName}</span></h1>
          <p className={cx('heroSub')}>Platform activity and key metrics at a glance.</p>
        </div>
      </section>

      {/* KPI grid */}
      <section className={cx('kpiGrid')} aria-label="Key metrics">
        <KpiCard label="Total users"    value={userCount}               eyebrow="Registered accounts"  variant="success" icon={<UsersIcon />}  />
        <KpiCard label="Total notes"    value={noteCount}               eyebrow="Created notes"         variant="primary" icon={<NotesIcon />}  />
        <KpiCard label="Subscriptions"  value={subscriptionCount}       eyebrow="Email waitlist"        variant="purple"  icon={<SubsIcon />}   />
        <KpiCard label="Active subs"    value={activeSubscriptionCount} eyebrow="Not unsubscribed"      variant="info"    icon={<ActiveIcon />} />
      </section>

      {/* Activity heatmap */}
      <section className={cx('heatmapSection')} aria-label="Platform activity heatmap">
        <h2 className={cx('heatmapTitle')}>Platform activity — past year</h2>
        <div className={cx('heatmapCard')}>
          <div className={cx('heatmapWrap')}>
            <HeatMap
              value={heatmapValue}
              startDate={heatmapStart}
              endDate={heatmapEnd}
              width="100%"
              style={{ color: 'var(--t-muted)' }}
              rectSize={12}
              space={3}
              rectProps={{ rx: 2 }}
              panelColors={{
                0: 'var(--heatmap-empty)',
                1: 'var(--heatmap-l1)',
                3: 'var(--heatmap-l2)',
                6: 'var(--heatmap-l3)',
                10: 'var(--heatmap-l4)',
              }}
            />
          </div>
        </div>
      </section>
    </>
  )
}

DashboardPage.layout = (page: React.ReactNode) => (
  <AdminLayout title="Overview">{page}</AdminLayout>
)

export default DashboardPage
